import { StoppingAreaDto } from 'api/stoppingAreas/models/StoppingAreaDto';
import StoppingAreasService from 'api/stoppingAreas/StoppingAreasService';
import Box from 'common/components/box/Box';
import { Col, Row } from 'react-flexbox-grid';
import ScreenContainer from 'common/components/screenContainer/ScreenContainer';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle'
import styles from './StoppingAreaScreen.module.scss';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import IconBack from 'assets/svg/Arrow-grey.svg';
import ScreenHeader from 'common/components/screenHeader/ScreenHeader';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import InputController from 'common/components/input/InputController';
import InputError from 'common/components/inputError/InputError';
import { useForm } from 'react-hook-form';
import Utils from 'common/services/Utils';
import InputGroupController from 'common/components/inputGroup/InputGroupController';
import Button from 'common/components/button/Button';
import ZoomLevelControl from 'common/components/maps/ZoomLevelControl';
import DrawTools, { LocationArea } from 'common/components/maps/DrawTools';
import { Circle, MapContainer, TileLayer } from 'react-leaflet';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';

export type Types = 'edit' | 'details' | 'new';

const StoppingAreaScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();
    const { type, itemId } = useParams<{ type: Types, itemId: string }>();
    const [item, setItem] = useState<StoppingAreaDto>();
    const [isDetails, setIsDetails] = useState<boolean>(type == 'details');
    const form = useForm<any>();
    const { handleSubmit, setValue, errors, register } = form;
    const [area, setArea] = useState<LocationArea>();
    const [position, setPosition] = useState<GeolocationPosition>();
    const [zoomDefault, setZoomDefault] = useState<number>(12);
    const [refresh, setRefresh] = useState<number>(1);
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = useState<boolean>(false);

    const getData = async () => {
            try {
                if (itemId) {
                    Loading.show();
                    const result = await StoppingAreasService.getById(itemId);
                    setItem(result);
                    Object.entries(result).forEach(([key, value]) => setValue(key, value));
                    if (result.latitude && result.longitude && result.radius) {                        
                        const areaAux = { latitude: result.latitude, longitude: result.longitude, mRadius: result.radius, radius: result.radius };
                        onCreateArea(areaAux);
                    }
                    setZoomDefault(result.zoom ?? zoomDefault);
                    onChangeCoordinates();
                    Loading.hide();
                    console.log(type)
                    console.log(isDetails)
                }
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.STOPPING_AREAS, `Couldn't get stopping areas information `, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            }
    }

    useEffect(() => {
        getData();
        setIsDetails(type=='details');
        navigator.geolocation.getCurrentPosition((x: GeolocationPosition) => setCurrentPosition(x));
    }, [itemId, type]);
    

    const navigateTo = (type?: string, id?: string) => {        
        if (type) {
            history.push(`/stopping-area/${type}/${id}`);
        } else {
             history.push(`/stopping-areas`);
        }
    }


    const onSubmit = async (formData: any) => {
        try {                 
            const model: StoppingAreaDto = {
                id: itemId ?? null,
                name: formData.name,                
                latitude: formData.latitude ?? undefined,
                longitude: formData.longitude ?? undefined,
                radius: formData.radius ?? undefined,
                zoom: formData.zoom ?? zoomDefault,
                rowVersion: item?.rowVersion,                                
            };

                if (itemId) {
                await StoppingAreasService.update(model)
                navigateTo('details', itemId)
            } else {
                const id = await StoppingAreasService.create(model);                    
                navigateTo('details', id)
            }
            
            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
        } catch (error) {
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.STOPPING_AREAS, `Couldn't update the stopping areas with id: ${itemId}`, error);
            Loading.hide();
        }
    }

    const removeItem = async (a?: StoppingAreaDto) => {
        setDialogDeleteItemIsOpen(false);
        if (!a) return;

        try {
            Loading.show();
            await StoppingAreasService.remove(a);
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            navigateTo();
        } catch (error) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    const onError = async () => {
        addToast(t('common.messages.validate_form'), { appearance: 'warning' });
    }

    const clearForm = () => {
        setValue('rowVersion', item?.rowVersion);        
        setValue('name', '');
        setValue('latitude', undefined);
        setValue('longitude', undefined);
        setValue('radius', undefined);
    };
    
    const clearArea = () => {
        setArea(undefined);
        setValue('latitude', '');
        setValue('longitude', '');
        setValue('radius', '');
    }
    
    const onCreateArea = (a: LocationArea) => {
        setArea(a);
        setValue('latitude', a.latitude);
        setValue('longitude', a.longitude);
        setValue('radius', a.mRadius);
    }
    
        const setCurrentPosition = (p: GeolocationPosition) => {
            setPosition(p);
            setRefresh(refresh + 1);
        }
    
        const onChangeCoordinates = () => {
            if (form.getValues('latitude') && form.getValues('longitude')) {
                const p: GeolocationPosition = {
                    coords: {
                        latitude: form.getValues('latitude'),
                        longitude: form.getValues('longitude'),
                        accuracy: 0,
                        altitude: null,
                        altitudeAccuracy: null,
                        heading: null,
                        speed: null
                    },
                    timestamp: Date.now()
                };
                setCurrentPosition(p);
                setZoomDefault(zoomDefault);
                if (form.getValues('radius')) {
                    setArea({ latitude: form.getValues('latitude'), longitude: form.getValues('longitude'), mRadius: form.getValues('radius'), radius: form.getValues('radius') });
                }
            }
        }

        const renderMap = () => {
            return <>
                <MapContainer
                    key={`map_${refresh}`}
                    center={[area ? area.latitude : position?.coords?.latitude ?? 52.516316, area ? area.longitude : position?.coords?.longitude ?? 13.377825]}
                    maxZoom={18} minZoom={6} zoom={zoomDefault} scrollWheelZoom={true} zoomSnap={0.1} zoomDelta={0.5} className={styles.map}>
                    {zoomDefault && <ZoomLevelControl onZoomChange={ zoom => { setZoomDefault(zoom); setValue('zoom', zoom); console.log(zoom); } } />}
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {area ? <Circle center={[area.latitude, area.longitude]} radius={area.mRadius}></Circle> :
                        (isDetails ? <></> : <DrawTools onCreated={onCreateArea} />)
                    }
                </MapContainer>
            </>
        }



    return (
        <ScreenTitle title={t('stopping_areas.title')}>
            <ScreenContainer> 
                <ScreenHeader title={t('stopping_areas.title')} />
                    <Box className={styles.box}>
                        <div>
                            <Row>
                                <Col xs={12} sm={12} md={12} lg={8} xl={9}>
                                    <div className={styles.header}>
                                        {type === 'new' &&
                                            <>
                                                <img src={IconBack} className={styles.icon} onClick={() => navigateTo()} /> {t('stopping_areas.new')}
                                            </>
                                        }
                                        {type == 'edit' &&
                                            <>
                                                <img src={IconBack} className={styles.icon} onClick={() => navigateTo()} /> {t('stopping_areas.edit')}
                                            </>}
                                        {type == 'details' &&
                                            <>
                                                <img src={IconBack} className={styles.icon} onClick={() => navigateTo()} /> {t('stopping_areas.details')}
                                            </>}
                                    </div>                                    
                                </Col>
                            </Row>
                            <form onSubmit={handleSubmit(onSubmit, onError)}>
                                <Row>
                                    <Col xs={12}>
                                        <FormItem>
                                            <Label className={styles.label}>{t('stopping_areas.list.name')}{!isDetails ? '*' : ''}</Label>
                                            <InputController
                                                name="name"
                                                form={form as any}
                                                autoComplete='off'
                                                disabled={isDetails}
                                                placeholder={t('stopping_areas.list.name')}
                                                rules={{ required: !(type === 'details'), validate: value => !Utils.isStringNullOrEmpty(value), maxLength: 250 }}
                                            />
                                            <InputError error={errors.name} maxLength={250} />
                                        </FormItem>                    
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={6} xl={3}>
                                        <Label className={styles.label}>{t('stopping_areas.latitude')}{!isDetails ? '*' : ''}</Label>
                                        <InputController                                                       
                                            name={'latitude'}
                                            type="number"
                                            min="0"
                                            form={form as any}
                                            autoComplete='off'
                                            disabled={isDetails}
                                            placeholder={t('stopping_areas.latitude')}
                                            rules={{ required: !isDetails }}
                                            onKeyPress={(e: any) => {
                                                if (e.key === 'Enter') {
                                                    onChangeCoordinates();
                                                }
                                            }}
                                            onBlur={() => {
                                                onChangeCoordinates();
                                            }}
                                        />
                                        <InputError error={errors['latitude']} />
                                    </Col>
                                    <Col xs={12} md={6} xl={3}>
                                        <Label className={styles.label}>{t('stopping_areas.longitude')}{!isDetails ? '*' : ''}</Label>
                                        <InputController                                                       
                                            name={'longitude'}
                                            type="number"
                                            min="0"
                                            form={form as any}
                                            autoComplete='off'
                                            disabled={isDetails}
                                            placeholder={t('stopping_areas.longitude')}
                                            rules={{ required: !isDetails }}
                                            onKeyPress={(e: any) => {
                                                if (e.key === 'Enter') {
                                                    onChangeCoordinates();
                                                }
                                            }}
                                            onBlur={() => {
                                                onChangeCoordinates();
                                            }}
                                        />
                                        <InputError error={errors['longitude']} />
                                    </Col>
                                    <Col xs={12} md={isDetails ? 6 : 4} xl={isDetails ? 3 : 2}>
                                        <Label className={styles.label}>{t('stopping_areas.radius')}{!isDetails ? '*' : ''}</Label>
                                        <InputGroupController
                                            name={'radius'}
                                            text={t('common.meters')}
                                            type="number"
                                            min="0"
                                            form={form as any}
                                            autoComplete='off'
                                            disabled={isDetails}
                                            placeholder={t('stopping_areas.radius')}
                                            rules={{ required: !isDetails }}
                                            onKeyPress={(e: any) => {
                                                if (e.key === 'Enter') {
                                                    onChangeCoordinates();
                                                }
                                            }}
                                            onBlur={() => {
                                                onChangeCoordinates();
                                            }}
                                        />                        
                                        <InputError error={errors['radius']} />
                                    </Col>
                                    { !isDetails && <Col xs={12} md={4} xl={2}>
                                        <div className={styles.smallButtonsContainer}>
                                            <Button
                                                preset={'danger'}
                                                type="button"
                                                onClick={clearArea}>
                                                {t('common.clear')}
                                            </Button>
                                        </div>
                                    </Col>}
                                </Row>

                                <Col xs={12}>
                                    <Row className={styles.map} >
                                        {renderMap()}
                                    </Row>
                                </Col>


                                <Row>
                                    <div className={styles.buttons}>
                                        <Button
                                            preset={'secondary'}
                                            type="button"
                                            onClick={() => {
                                                if (!!itemId) {
                                                    isDetails ? navigateTo() : navigateTo('details', itemId);
                                                } else {
                                                    history.goBack();
                                                }
                                            }}
                                            text={t('common.cancel')}
                                        />
                                        {!!itemId && isDetails &&
                                            <Button
                                                type="button"
                                                text={t('common.remove')}
                                                preset={'danger'}
                                                onClick={() => setDialogDeleteItemIsOpen(true)} />
                                        }
                                        {itemId && isDetails &&
                                            <Button
                                                type={'button'}
                                                text={t('common.edit')}
                                                onClick={() => {                                                    
                                                    navigateTo('edit', itemId);
                                                }}
                                            />}
            
                                        {!isDetails && <Button
                                            text={t('common.save')}
                                            onClick={handleSubmit(onSubmit)}
                                        />}
            
                                        {item && <QuestionYesNo message={t('common.messages.remove_record')}
                                            isVisible={dialogDeleteItemIsOpen}
                                            onYes={() => removeItem(item)}
                                            onNo={() => setDialogDeleteItemIsOpen(false)} />}
                                    </div>
                                </Row>


                            </form>
                        </div>
                    </Box>
            </ScreenContainer>
        </ScreenTitle>
    )
}

export default StoppingAreaScreen